import { Button } from '@lfg/ui/components';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Stack, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { Show } from 'src/components/Show';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { isAllowedGreadterThanZero } from 'src/utils/numberFormat';

import { AdminNumberFormat } from '../AdminNumberFormat';

interface TierItemProps {
  index: number;
  isModifying: boolean;
  onEdit: () => void;
  onSave: () => void;
  onDelete: (e: React.MouseEvent<HTMLButtonElement | HTMLOrSVGElement>) => void;
}
export function TierItem({ index, isModifying, onDelete, onEdit, onSave }: TierItemProps) {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<ProjectInputs>();
  const currentLayer = watch(`tier.layer.${index}`);
  const { hasDeployed } = useDeployment();

  return (
    <Box width={'100%'} marginBottom={2} padding={'16px 24px'} border={`1px solid ${colors.neutral015}`}>
      {isModifying ? (
        <Box>
          <Typography fontWeight={500} fontSize={'16px'} color='white' paddingBottom={2}>
            Tier {index + 1}
          </Typography>
          <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
            NFT Supply for this Tier
          </Typography>
          <Controller
            name={`tier.layer.${index}.total`}
            control={control}
            render={({ field: { onChange, onBlur, ...rest } }) => (
              <AdminNumberFormat
                suffix=''
                prefix=''
                decimalScale={0}
                disabled={hasDeployed}
                isAllowed={isAllowedGreadterThanZero}
                onValueChange={values => {
                  onChange(Number(values.floatValue) || 0);
                }}
                isError={!!errors?.tier?.layer?.[index]?.total}
                errorMsg={errors?.tier?.layer?.[index]?.total?.message}
                placeholder={'E.g., 100'}
                {...rest}
              />
            )}
          />
          <Typography paddingBottom={1} paddingTop={2} color={colors.neutral200} fontSize={'14px'} variant='h6'>
            Price in USD
          </Typography>
          <Controller
            name={`tier.layer.${index}.price`}
            control={control}
            render={({ field }) => (
              <AdminNumberFormat
                suffix=''
                prefix=''
                disabled={hasDeployed}
                isAllowed={isAllowedGreadterThanZero}
                onValueChange={values => {
                  field.onChange(Number(values.floatValue) || 0);
                }}
                isError={!!errors?.tier?.layer?.[index]?.price}
                errorMsg={errors?.tier?.layer?.[index]?.price?.message}
                placeholder={'0.0'}
                {...field}
              />
            )}
          />
          <Stack flexDirection={'row'} justifyContent={'end'} paddingTop={3} gap={1}>
            <Button
              bgColor={colors.neutral06}
              onClick={onDelete}
              sx={{
                padding: '10px 32px !important',
                fontWeight: '500 !important',
                lineHeight: '20px !important',
                borderRadius: '4px !important',
                height: '48px !important',
              }}>
              Cancel
            </Button>
            <Button
              bgColor={`${colors.brand300}`}
              onClick={onSave}
              sx={{
                padding: '10px 32px !important',
                fontWeight: '500 !important',
                lineHeight: '20px !important',
                borderRadius: '4px !important',
                height: '48px !important',
              }}>
              Save
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
            <Stack direction={'row'} alignItems={'center'} gap={3}>
              <img src={'/icons/common/setting.svg'} width={24} height={24} alt='tier icon' />
              <Box>
                <Typography fontWeight={400} fontSize={'14px'} color='white' paddingBottom={1}>
                  Tier: {index + 1} &nbsp; &nbsp; Supply: {currentLayer.total}
                </Typography>
                <Typography color={colors.neutral90} fontSize={'14px'}>
                  Price: {currentLayer?.price}
                </Typography>
              </Box>
            </Stack>
            <Show when={!hasDeployed}>
              <Stack direction={'row'} gap={1}>
                <BorderColorIcon
                  onClick={onEdit}
                  className='cursor-pointer'
                  sx={{
                    cursor: 'pointer',
                    color: colors.neutral200,
                  }}
                />
                <DeleteIcon
                  onClick={onDelete}
                  className='cursor-pointer'
                  sx={{
                    cursor: 'pointer',
                    color: colors.error,
                  }}
                />
              </Stack>
            </Show>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
